import Image from "next/image";
import { getStampLayout } from "@/lib/constants";

interface LoyaltyCardProps {
  name: string;
  background: string;
  color: string;
  pictureSrc: string;
  description: string;
  pointsRequired: number;
  rewardEmoji: string;
  pointsCollected?: number;
}

// Strip is always h-28 (112px). Circle size scales to fill rows evenly.
const STRIP_H = 112;
const STRIP_PADDING_V = 16; // p-1 top + p-1 bottom = 4+4
const GAP = 4; // gap-1
// Maximum circle size = size at the default 9 pointsRequired (2 rows): floor((112-8-4)/2) = 50px
const MAX_CIRCLE_SIZE = 50;

function calcCircleSize(rows: number): number {
  const size = Math.floor(
    (STRIP_H - STRIP_PADDING_V - GAP * (rows - 1)) / rows,
  );
  return Math.min(size, MAX_CIRCLE_SIZE);
}

const LoyaltyCard = ({
  name,
  background,
  color,
  pictureSrc,
  description,
  pointsRequired,
  rewardEmoji,
  pointsCollected = 0,
}: LoyaltyCardProps) => {
  const safePoints = Math.max(2, Math.min(14, pointsRequired || 9));
  const { cols, rows } = getStampLayout(safePoints);
  const circleSize = calcCircleSize(rows);
  const fontSize = Math.max(10, Math.floor(circleSize * 0.55));

  return (
    <div
      style={{ backgroundColor: background }}
      className="w-full sm:max-w-sm h-full sm:shadow-xl "
    >
      {/* Card header */}
      <div className="flex items-center justify-between gap-2 p-5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7.5 h-7.5 relative shrink-0">
            <Image src={pictureSrc} unoptimized alt="venue logo" fill />
          </div>
          <span style={{ color }} className="font-bold truncate">
            {name}
          </span>
        </div>
        <div className="text-right shrink-0">
          <p
            style={{ color }}
            className="text-xs uppercase tracking-wide opacity-60"
          >
            Stamps
          </p>
          <p style={{ color }} className="text-sm font-semibold">
            {pointsCollected}/{safePoints}
          </p>
        </div>
      </div>

      {/* Stamp strip — fixed h-28, circles scale to fit */}
      <div
        style={{
          backgroundColor: background,
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        }}
        className="h-28 p-2 gap-1 grid content-start"
      >
        {Array.from({ length: safePoints }).map((_, i) => (
          <div
            key={i}
            style={{
              width: circleSize,
              height: circleSize,
              borderColor: color,
            }}
            className="border-2 rounded-full mx-auto"
          />
        ))}
        <div
          style={{
            width: circleSize,
            height: circleSize,
            fontSize,
            backgroundColor: color,
            borderColor: color,
          }}
          className="border-2 rounded-full mx-auto flex items-center justify-center"
        >
          <span>{rewardEmoji}</span>
        </div>
      </div>

      {/* Card body */}
      <div className="px-5 py-3 text-left">
        <p
          style={{ color }}
          className="text-xs uppercase tracking-wide opacity-60"
        >
          Reward
        </p>
        <p style={{ color }} className="text-sm font-medium">
          {description || "Free Item"}
        </p>
      </div>
    </div>
  );
};

export default LoyaltyCard;
